'use client';

/**
 * Auth Screen Component
 * 
 * A beautiful tabbed login/register screen with smooth animations.
 * Used on the account page when user is not authenticated.
 */

import { useState } from 'react';
import { LoginForm } from './LoginForm';
import { RegisterForm } from './RegisterForm';
import { Building2, Heart, FileText, Bell } from 'lucide-react';

type AuthTab = 'login' | 'register';

interface AuthScreenProps {
  /** Default active tab */
  defaultTab?: AuthTab;
  /** URL to redirect after successful auth */
  redirectTo?: string;
  /** Custom class name */
  className?: string;
}

export function AuthScreen({
  defaultTab = 'login',
  redirectTo = '/account',
  className = '',
}: AuthScreenProps) {
  const [activeTab, setActiveTab] = useState<AuthTab>(defaultTab);

  const benefits = [
    {
      icon: Heart,
      title: 'Save Favourites',
      description: 'Create wishlists and save properties you love',
    },
    {
      icon: FileText,
      title: 'Make Offers',
      description: 'Submit and track offers on properties',
    },
    {
      icon: Building2,
      title: 'Book Viewings',
      description: 'Schedule viewings with agents',
    },
    {
      icon: Bell,
      title: 'Get Alerts',
      description: 'Receive notifications on price changes',
    },
  ];

  return (
    <div className={`auth-screen ${className}`}>
      {/* Tab Switcher */}
      <div className="relative mb-8">
        <div className="flex bg-muted/50 rounded-xl p-1">
          <button
            onClick={() => setActiveTab('login')}
            className={`
              relative flex-1 py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-200
              ${activeTab === 'login' 
                ? 'text-foreground' 
                : 'text-muted-foreground hover:text-foreground/80'
              }
            `}
          >
            {activeTab === 'login' && (
              <span 
                className="absolute inset-0 bg-background rounded-lg shadow-sm"
                style={{
                  animation: 'tabSlide 200ms ease-out',
                }}
              />
            )}
            <span className="relative z-10">Sign In</span>
          </button>
          <button
            onClick={() => setActiveTab('register')}
            className={`
              relative flex-1 py-3 px-4 rounded-lg text-sm font-semibold transition-all duration-200
              ${activeTab === 'register' 
                ? 'text-foreground' 
                : 'text-muted-foreground hover:text-foreground/80'
              }
            `}
          >
            {activeTab === 'register' && (
              <span 
                className="absolute inset-0 bg-background rounded-lg shadow-sm"
                style={{
                  animation: 'tabSlide 200ms ease-out',
                }}
              />
            )}
            <span className="relative z-10">Create Account</span>
          </button>
        </div>
      </div>

      {/* Auth Forms */}
      <div className="relative min-h-[400px]">
        {activeTab === 'login' ? (
          <div
            key="login"
            style={{
              animation: 'fadeSlideIn 300ms ease-out',
            }}
          >
            <LoginForm
              redirectTo={redirectTo}
              showRegisterLink={false}
              showSocialAuth={true}
              title="Welcome Back"
              subtitle="Sign in to access your account"
            />
          </div>
        ) : (
          <div
            key="register"
            style={{
              animation: 'fadeSlideIn 300ms ease-out',
            }}
          >
            <RegisterForm
              redirectTo={redirectTo}
              showLoginLink={false}
              showSocialAuth={true}
              title="Create Account"
              subtitle="Join us to unlock all features"
            />
          </div>
        )}
      </div>

      {/* Benefits Section */}
      <div className="mt-10 pt-8 border-t border-border/50">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-4 text-center">
          Member Benefits
        </p>
        <div className="grid grid-cols-2 gap-3">
          {benefits.map((benefit) => (
            <div
              key={benefit.title}
              className="flex items-start gap-3 p-3 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors"
            >
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <benefit.icon className="w-4 h-4 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {benefit.title}
                </p>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes tabSlide {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

export default AuthScreen;
